import React from "react";
import { Modal } from "@truevault/aquilo";
import { ClockIcon, EnvelopeIcon } from "@heroicons/react/24/outline";
import { Link } from "react-router-dom";
import { Routes } from "../../../root/routes";

type ReportGeneratingModalProps = {
  /** Human-readable report name, e.g. "GPC Opt-Out Records" */
  reportName: string;
  onClose: () => void;
};

/**
 * Confirmation shown after a CSV export job is queued (consent records, GPC opt-outs,
 * record lookup). The export runs async and the user is emailed when it's ready.
 */
export const ReportGeneratingModal = ({ reportName, onClose }: ReportGeneratingModalProps) => {
  return (
    <Modal
      headerIcon={
        <img
          className="h-[150px] w-[240px]"
          src="/assets/images/stay_compliant/documents/report-generating-modal-illustration.svg"
          alt=""
        />
      }
      title={`Your ${reportName} CSV is on its way!`}
      alignText="center"
      firstButton="Got it"
      firstButtonColor="primary"
      widthButton="full"
      // The close button adds a right margin to the content; drop it so the centered layout stays centered.
      contentContainerClasses="w-full !mr-0"
      contentPadding="px-[24px] pt-6 pb-0"
      buttonPadding="px-[24px] pt-2 pb-[24px]"
      showOverlay
      closeButton
      onClose={onClose}
      onFirstButtonClick={onClose}
    >
      <ul className="mt-2 w-full space-y-3 rounded-lg border border-primary-100 bg-primary-50 p-4 text-left">
        <li className="flex items-start gap-3">
          <span className="flex h-8 w-8 flex-none items-center justify-center rounded-full bg-primary-100">
            <ClockIcon className="h-5 w-5 text-primary-600" aria-hidden="true" />
          </span>
          <p className="pt-1.5 text-sm text-gray-700">
            We&apos;re putting your report together. Depending on how many records it includes, this
            can take up to 1 hour.
          </p>
        </li>
        <li className="flex items-start gap-3">
          <span className="flex h-8 w-8 flex-none items-center justify-center rounded-full bg-primary-100">
            <EnvelopeIcon className="h-5 w-5 text-primary-600" aria-hidden="true" />
          </span>
          <p className="pt-1.5 text-sm text-gray-700">
            We&apos;ll email you as soon as it&apos;s ready. You can also download it anytime from{" "}
            <Link
              className="whitespace-nowrap font-medium text-primary-700 underline"
              to={Routes.documents.recordExports}
              onClick={onClose}
            >
              Privacy Docs → Record Exports
            </Link>
            .
          </p>
        </li>
      </ul>
    </Modal>
  );
};
